import { useEffect, useState } from "react";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";

// Shared by the student sidebar and the admin sidebar: one preference, one
// animation, so both menus fold the same way.

const STORAGE_KEY = "hey404:sidebar-collapsed";

/** Desktop sidebar collapsed to icons; remembered across visits. */
export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "1",
  );
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);
  return [collapsed, setCollapsed] as const;
}

// Sidebar labels: a subtle swipe left (fade + slide) when the menu collapses,
// back right when it expands. Width goes to 0 so the icons centre; opacity
// (not display) keeps the names available to screen readers.
export const sidebarLabel = (collapsed: boolean) =>
  `overflow-hidden whitespace-nowrap transition-[opacity,translate,max-width] duration-300 ease-[cubic-bezier(0.22,0.8,0.25,1)] ${
    collapsed
      ? "pointer-events-none max-w-0 -translate-x-3 opacity-0"
      : "max-w-56 translate-x-0 opacity-100"
  }`;

/**
 * The "Colapsar menú" / expand toggle at the foot of a sidebar: full sidebar
 * width, shaped like the nav links, under its own divider.
 */
export function CollapseToggle({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-paper/20 w-full border-t py-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}
        title={collapsed ? "Expandir menú" : undefined}
        className={`rounded-control text-paper/90 hover:bg-paper/15 hover:text-paper flex min-h-11 w-full cursor-pointer items-center py-2.5 font-mono text-xs font-bold transition-[background-color,color,gap,padding] duration-300 ${collapsed ? "justify-center gap-0 px-0" : "gap-3 px-3"}`}
      >
        {collapsed ? (
          <PanelLeftOpen size={18} aria-hidden="true" className="shrink-0" />
        ) : (
          <PanelLeftClose size={18} aria-hidden="true" className="shrink-0" />
        )}
        <span aria-hidden="true" className={sidebarLabel(collapsed)}>
          Colapsar menú
        </span>
      </button>
    </div>
  );
}
